import React from 'react';
import { ThumbnailsWrapperInner, HeightAwareSliderItemContainer } from './styled';
import { IThumbnail } from 'utils/entities';
import { ViewPager } from 'components/view-pager';
import { shouldLazilyLoad } from 'utils/gallery';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { IMAGE_PIXEL_DENSITY } from 'components/popup-image-gallery/constants';
import { rangeToGalleryHeight } from './config';


interface ImageGalleryProps {
  images: IThumbnail[];
  activeSlide: number;
  handleSlideIndexChange(idx: number): void;
  openFullscreen: (imagesList: IThumbnail[], idx: number) => void;
  altText?: string;
  height?: number;
}

const LAZY_LOAD_MARGIN = 3;

interface SliderItemProps {
  url: string;
  shouldLoad: boolean;
  isFloorPlan: boolean;
  rotateDegrees: number;
  altText?: string;
  handleClick?: () => void;
  height?: number;
}

const SliderItem: React.FC<SliderItemProps> = ({
  url,
  shouldLoad,
  isFloorPlan,
  rotateDegrees,
  altText,
  handleClick,
  height,
}) => {
  const breakpointValue = useScreenBreakpoint();
  const constructImageURI = useImageUrlBuilder();
  const uri = constructImageURI(url, {
    height: (height || rangeToGalleryHeight[breakpointValue]) * IMAGE_PIXEL_DENSITY,
    rotateDegrees,
  });

  return (
    <HeightAwareSliderItemContainer isFloorPlan={isFloorPlan}>
      <div
        data-auto="bulletin-image"
        data-auto-url={uri}
      >
        {shouldLoad ? <img src={uri} onClick={handleClick} alt={altText || undefined} /> : null}
      </div>
    </HeightAwareSliderItemContainer>
  );
};


export const ImageGallery: React.FunctionComponent<ImageGalleryProps> = (props) => {
  const {
    images,
    activeSlide,
    handleSlideIndexChange,
    openFullscreen,
    altText,
    height,
  } = props;

  return (
    <ThumbnailsWrapperInner>
      <ViewPager
        align="center"
        slideIndex={activeSlide}
        onSlideIndexChange={handleSlideIndexChange}
      >
        {images.map((image, i) => {
          const { url, isFloorPlan, rotateDegrees } = image;
          const shouldLoad = shouldLazilyLoad(activeSlide, i, images.length, LAZY_LOAD_MARGIN);

          return (
            <SliderItem
              key={`${url}-${i}`}
              url={url}
              isFloorPlan={isFloorPlan}
              shouldLoad={shouldLoad}
              handleClick={() => openFullscreen(images, i)}
              rotateDegrees={rotateDegrees}
              altText={altText}
              height={height}
            />
          );
        })}
      </ViewPager>
    </ThumbnailsWrapperInner>
  );
};
